/**
 * Carryover Copy Utility
 * =======================
 * 
 * Generates personality-driven, state-aware copy for carryover task experience.
 * 
 * Core Principle: RESET FRAMING, not DEFICIT FRAMING
 * - Deficit: "You didn't finish"
 * - Reset: "Let's adjust the plan"
 * 
 * Language Rules:
 * - DO NOT use: "Incomplete", "Missed", "Not done", "From yesterday"
 * - DO use: "Fresh start", "Reset", "Adjust", "Carryovers", "Tune-up"
 */

export interface CarryoverCopy {
  banner: {
    title: string
    body: string
    primaryLabel: string
    secondaryLabel: string
  }
  modal: {
    title: string
    body: string
    primaryLabel: string
    secondaryLabel: string
    microcopy?: string
  }
  autoSchedule: {
    label: string
    body: string
    confirmationTitle: string
    confirmationBody: string
  }
}

type Personality = 'supportive_best_friend' | 'calm_older_sibling' | 'quiet_mentor'

interface CarryoverContext {
  personality: Personality
  userState: string
  count: number
}

/**
 * Get personality-driven and state-aware copy for carryover experience.
 * 
 * Override Rules:
 * 1. user_state === 'overwhelmed' → Use softened language
 * 2. user_state === 'recovery' → Use softened language
 * 3. count >= 5 → Use softened language (volume-based override)
 * 
 * Task volume creates emotional load independent of state machine.
 * State ALWAYS overrides personality.
 */
export function getCarryoverUX({
  personality,
  userState,
  count
}: CarryoverContext): CarryoverCopy {
  // State override check
  const shouldSoften = 
    userState === 'overwhelmed' || 
    userState === 'recovery' || 
    count >= 5
  
  if (shouldSoften) {
    return getOverrideCopy(count)
  }
  
  // Return personality-specific variant
  switch (personality) {
    case 'supportive_best_friend':
      return getSupportiveBestFriendCopy(count)
    case 'calm_older_sibling':
      return getCalmOlderSiblingCopy(count)
    case 'quiet_mentor':
      return getQuietMentorCopy(count)
    default:
      return getSupportiveBestFriendCopy(count)
  }
}

/**
 * OVERWHELMED/RECOVERY/HIGH-VOLUME OVERRIDE
 * 
 * When user is overwhelmed, in recovery, or has 5+ carryovers:
 * - Remove or minimize emojis
 * - Reduce language intensity
 * - Focus on simplicity
 * - Default CTA: "Keep it simple"
 */
function getOverrideCopy(count: number): CarryoverCopy {
  const itemText = count === 1 ? 'item is' : 'items are'
  
  return {
    banner: {
      title: "Let's keep today simple.",
      body: `A ${count === 1 ? 'task' : 'couple of tasks'} ${itemText} still open. We can lighten the load.`,
      primaryLabel: "Keep it simple",
      secondaryLabel: "Not now"
    },
    modal: {
      title: "Let's keep this manageable.",
      body: "We'll keep this simple. Choose what feels easiest.",
      primaryLabel: "Keep it simple",
      secondaryLabel: "I'll handle it"
    },
    autoSchedule: {
      label: "Let me handle the scheduling",
      body: "I'll spread these across your week so nothing piles up.",
      confirmationTitle: "All set.",
      confirmationBody: "I've found good times for your tasks. Check Review Schedule to adjust."
    }
  }
}

/**
 * SUPPORTIVE BEST FRIEND
 * 
 * Tone: Encouraging, warm, enthusiastic (but not overwhelming)
 * Energy: High positive energy
 * Focus: Emotional support, validation
 */
function getSupportiveBestFriendCopy(count: number): CarryoverCopy {
  return {
    banner: {
      title: "Fresh start for today ✨",
      body: `Yesterday was full. Totally okay. ${count === 1 ? 'One thing is' : 'A few things are'} still open. Want help reshaping today?`,
      primaryLabel: "Help me adjust",
      secondaryLabel: "Not now"
    },
    modal: {
      title: "Let's make today feel doable 💛",
      body: "No pressure — we'll adjust things gently.",
      primaryLabel: "Help me adjust",
      secondaryLabel: "I've got this"
    },
    autoSchedule: {
      label: "Find good times for me",
      body: "I'll fit these into your week — no stress.",
      confirmationTitle: "You're all set! ✨",
      confirmationBody: "I've found great times for your tasks. Head to Review Schedule to see or tweak them."
    }
  }
}

/**
 * CALM OLDER SIBLING
 * 
 * Tone: Steady, practical, directive (but supportive)
 * Energy: Calm confidence
 * Focus: Process, organization, clear next steps
 */
function getCalmOlderSiblingCopy(count: number): CarryoverCopy {
  const itemText = count === 1 ? 'item is' : 'items are'
  
  return {
    banner: {
      title: "Let's reset for today.",
      body: `A ${count === 1 ? 'task' : 'couple of tasks'} ${itemText} still open. Want help making today manageable?`,
      primaryLabel: "Help me adjust",
      secondaryLabel: "Not now"
    },
    modal: {
      title: "Quick reset.",
      body: "No big deal — we'll adjust the plan step by step.",
      primaryLabel: "Help me adjust",
      secondaryLabel: "I'll handle it"
    },
    autoSchedule: {
      label: "Auto-schedule these for me",
      body: "I'll slot them into your upcoming days.",
      confirmationTitle: "Done.",
      confirmationBody: "Tasks are scheduled across your week. Review Schedule has the details."
    }
  }
}

/**
 * QUIET MENTOR
 * 
 * Tone: Reflective, mindful, exploratory
 * Energy: Gentle, patient, spacious
 * Focus: Awareness, choice, what feels right
 */
function getQuietMentorCopy(count: number): CarryoverCopy {
  const itemText = count === 1 ? 'item is' : 'items are'
  
  return {
    banner: {
      title: "A small reset?",
      body: `A ${count === 1 ? 'task' : 'few tasks'} ${itemText} still open. Want to revisit ${count === 1 ? 'it' : 'them'} — or let ${count === 1 ? 'it' : 'them'} wait?`,
      primaryLabel: "Help me adjust",
      secondaryLabel: "Not now"
    },
    modal: {
      title: "What feels right today?",
      body: "We can reshape the plan, or give these space.",
      primaryLabel: "Help me adjust",
      secondaryLabel: "Let me think"
    },
    autoSchedule: {
      label: "Find times that feel right",
      body: "I'll weave these into your week gently.",
      confirmationTitle: "All arranged.",
      confirmationBody: "Your tasks have new homes in your schedule. Take a look when you're ready."
    }
  }
}

/**
 * Validation: Ensure no forbidden language appears in copy.
 * This is a safety check - should never trigger if copy is correct.
 */
export function validateCarryoverCopy(copy: CarryoverCopy): boolean {
  const forbiddenWords = ['incomplete', 'missed', 'not done', 'from yesterday', 'failed', 'behind']
  const allText = [
    copy.banner.title,
    copy.banner.body,
    copy.banner.primaryLabel,
    copy.banner.secondaryLabel,
    copy.modal.title,
    copy.modal.body,
    copy.modal.primaryLabel,
    copy.modal.secondaryLabel,
    copy.autoSchedule.label,
    copy.autoSchedule.body,
    copy.autoSchedule.confirmationTitle,
    copy.autoSchedule.confirmationBody,
  ].join(' ').toLowerCase()
  
  for (const word of forbiddenWords) {
    if (allText.includes(word)) {
      console.error(`Forbidden word "${word}" found in carryover copy!`)
      return false
    }
  }
  
  return true
}
